import { Analytics } from '@cobliteam/analytics';
import Button from '@cobliteam/colib-ui/Button';
import Spinner from '@cobliteam/colib-ui/Spinner';
import Switch from '@cobliteam/colib-ui/Switch';
import { useTranslation } from '@cobliteam/i18n';
import Dialog from '@cobliteam/ui/components/Dialog';
import Typography from '@cobliteam/ui/components/Typography';
import InputMask from '@cobliteam/ui/form/InputMask';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import { type FormEvent, useEffect, useRef, useState } from 'react';

interface AIUserInviteModalProps {
  fleetId?: string;
  userId?: string;
  initialPhoneNumber?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBetaOptInChange: (enabled: boolean) => Promise<void>;
  onPhoneNumberUpdate: (phoneNumber: string) => Promise<void> | void;
}

const PHONE_REPLACEMENT = { _: /\d/ };
const CONFETTI_COLORS = ['#003FFF', '#0057FF', '#6EA8FF', '#DCE9FF', '#FFC83D'];

const fireInviteConfetti = async () => {
  if (typeof window === 'undefined') return;

  const { default: confetti } = await import('canvas-confetti');
  const sharedOptions = {
    colors: CONFETTI_COLORS,
    disableForReducedMotion: true,
    scalar: 0.95,
    startVelocity: 48,
    ticks: 220,
    zIndex: 10000,
  };

  void confetti({
    ...sharedOptions,
    angle: 64,
    origin: { x: 0.12, y: 0.16 },
    particleCount: 140,
    spread: 82,
  });

  void confetti({
    ...sharedOptions,
    angle: 116,
    origin: { x: 0.88, y: 0.16 },
    particleCount: 140,
    spread: 82,
  });

  window.setTimeout(() => {
    void confetti({
      ...sharedOptions,
      decay: 0.92,
      origin: { x: 0.5, y: 0.16 },
      particleCount: 110,
      spread: 112,
      startVelocity: 38,
    });
  }, 140);

  window.setTimeout(() => {
    void confetti({
      ...sharedOptions,
      decay: 0.94,
      origin: { x: 0.5, y: 0.08 },
      particleCount: 80,
      scalar: 0.78,
      spread: 140,
      startVelocity: 30,
    });
  }, 320);
};

const dismissInviteModal = (storageKey: string, onOpenChange: (open: boolean) => void) => {
  dismissModal(storageKey);
  onOpenChange(false);
};

const closeInviteModal = (onOpenChange: (open: boolean) => void, nextOpen: boolean) => {
  onOpenChange(nextOpen);
};

const shouldRenderModal = (storageKey: string) => {
  if (typeof window === 'undefined') return false;
  return !wasDismissed(storageKey);
};

const useInviteConfetti = (enabled: boolean) => {
  const hasFiredConfettiRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      hasFiredConfettiRef.current = false;
      return;
    }

    if (hasFiredConfettiRef.current) return;

    hasFiredConfettiRef.current = true;
    void fireInviteConfetti();
  }, [enabled]);
};

const useTrackInviteDisplayed = (enabled: boolean) => {
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      hasTrackedRef.current = false;
      return;
    }

    if (hasTrackedRef.current) return;

    hasTrackedRef.current = true;
    Analytics.track('AI - Invite Modal - Displayed');
  }, [enabled]);
};

const renderHighlightedText = (text: string, highlight: string) => {
  const brandIndex = text.indexOf(highlight);

  if (brandIndex === -1) return text;

  return (
    <>
      {text.slice(0, brandIndex)}
      <span className='font-bold text-primary'>{highlight}</span>
      {text.slice(brandIndex + highlight.length)}
    </>
  );
};

const benefits: Array<{
  icon: IconNames;
  titleKey: string;
  descriptionKey: string;
}> = [
  {
    icon: 'calendar-days',
    titleKey: 'invite_modal.benefits.early_access.title',
    descriptionKey: 'invite_modal.benefits.early_access.description',
  },
  {
    icon: 'message-square-text',
    titleKey: 'invite_modal.benefits.opinion.title',
    descriptionKey: 'invite_modal.benefits.opinion.description',
  },
  {
    icon: 'shield-check',
    titleKey: 'invite_modal.benefits.impact.title',
    descriptionKey: 'invite_modal.benefits.impact.description',
  },
];

const getStorageKey = (userId?: string, fleetId?: string) => `ai-user-invite-modal:${userId ?? 'unknown-user'}:${fleetId ?? 'unknown-fleet'}`;

const wasDismissed = (storageKey: string) => {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(storageKey) === 'dismissed';
};

const dismissModal = (storageKey: string) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(storageKey, 'dismissed');
};

const AIUserInviteModal = ({ fleetId, userId, initialPhoneNumber, open, onOpenChange, onBetaOptInChange, onPhoneNumberUpdate }: AIUserInviteModalProps) => {
  const { t } = useTranslation('ai');
  const [phone, setPhone] = useState(initialPhoneNumber ?? '');
  const [wantsBeta, setWantsBeta] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const storageKey = getStorageKey(userId, fleetId);
  const isRenderable = shouldRenderModal(storageKey);
  const title = t('invite_modal.title');
  const titleHighlight = t('invite_modal.title_highlight');

  useInviteConfetti(open && isRenderable);
  useTrackInviteDisplayed(open && isRenderable);

  if (!isRenderable) return null;

  const handleOpenChange = (nextOpen: boolean) => {
    closeInviteModal(onOpenChange, nextOpen);
  };

  const handleDismiss = () => {
    Analytics.track('AI - Invite Modal - Dismissed');
    dismissInviteModal(storageKey, onOpenChange);
  };

  const handleBetaToggle = (enabled: boolean) => {
    Analytics.track('AI - Invite Modal - Beta Toggled', { enabled });
    setWantsBeta(enabled);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onPhoneNumberUpdate(phone.trim());
      await onBetaOptInChange(wantsBeta);
      Analytics.track('AI - Invite Modal - Submitted', { wantsBeta });
      dismissInviteModal(storageKey, onOpenChange);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleSubmit();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content size='lg' className='max-h-[calc(100vh-32px)] w-[min(900px,calc(100vw-32px))]! max-w-[calc(100vw-32px)] overflow-hidden rounded-3xl p-0'>
        <Dialog.Title className='sr-only'>{title}</Dialog.Title>
        <Dialog.Description className='sr-only'>{t('invite_modal.description')}</Dialog.Description>
        <button
          type='button'
          aria-label={t('invite_modal.close')}
          className='absolute top-5 right-5 z-10 flex size-9 items-center justify-center rounded-xl border border-primary-6 bg-white/80 text-gray-10 shadow-sm transition-colors hover:bg-primary-2 hover:text-gray-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-7'
          onClick={handleDismiss}
        >
          <Icon name='x' aria-hidden='true' className='size-5' />
        </button>
        <Dialog.Body className='flex-1 p-0'>
          <div className='grid max-h-[calc(100vh-32px)] laptop:grid-cols-[410px_330px] justify-center gap-7 overflow-y-auto overscroll-contain p-10'>
            <form className='flex min-w-0 max-w-[410px] flex-col' onSubmit={handleFormSubmit}>
              <div className='mb-6 flex h-10 w-fit max-w-full items-center gap-3 rounded-xl border border-primary-6 bg-primary-2 pr-4 pl-2 text-primary'>
                <span className='flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-white'>
                  <Icon name='star' aria-hidden='true' className='size-4 fill-current' />
                </span>
                <Typography variant='caption-xs-semibold' className='whitespace-nowrap font-bold text-xs uppercase leading-none tracking-normal'>
                  {t('invite_modal.badge')}
                </Typography>
              </div>

              <Typography as='h2' variant='heading-4xl' className='max-w-[410px] text-balance font-bold text-[1.75rem] text-gray-12 leading-[1.15] tracking-normal'>
                {renderHighlightedText(title, titleHighlight)}
              </Typography>
              <Typography variant='text-lg' className='mt-5 max-w-[410px] text-pretty font-normal text-base text-gray-11! leading-[1.45] tracking-normal'>
                {t('invite_modal.description')}
              </Typography>

              <div className='mt-7 mb-5 h-px bg-gray-4' />

              <div className='flex flex-col gap-2'>
                <Typography as='label' htmlFor='ai-user-invite-phone' variant='text-md-semibold' className='font-bold text-[0.9375rem] text-gray-12 leading-[1.35] tracking-normal'>
                  {t('invite_modal.phone_label')}
                </Typography>
                <div className='flex h-12 overflow-hidden rounded-lg border border-gray-6 bg-white focus-within:border-gray-8 focus-within:shadow'>
                  <div className='flex w-[116px] shrink-0 items-center justify-center gap-3 border-gray-5 border-r bg-gray-1 px-4 text-gray-12 text-sm'>
                    <span aria-hidden='true'>🇧🇷</span>
                    <span className='font-medium'>{t('invite_modal.country_code')}</span>
                    <Icon name='chevron-down' aria-hidden='true' className='size-4 text-gray-10' />
                  </div>
                  <div className='min-w-0 flex-1'>
                    <InputMask
                      id='ai-user-invite-phone'
                      name='phone'
                      type='tel'
                      inputMode='tel'
                      autoComplete='tel-national'
                      value={phone}
                      onChange={(event) => setPhone(event.target.value)}
                      mask='(__) _ ____-____'
                      replacement={PHONE_REPLACEMENT}
                      placeholder={t('invite_modal.phone_placeholder')}
                      className='h-12 rounded-none border-0 px-4 text-sm shadow-none focus:border-0 focus:shadow-none focus-visible:border-0'
                    />
                  </div>
                </div>
              </div>

              <div className='mt-6 h-px bg-gray-4' />

              <div className='mt-6 flex items-start justify-between gap-5'>
                <div className='flex flex-col gap-1'>
                  <Typography variant='text-md-semibold' className='font-bold text-[0.9375rem] text-gray-12 leading-[1.35] tracking-normal'>
                    {t('invite_modal.beta_label')}
                  </Typography>
                  <Typography variant='text-md' className='font-normal text-[0.9375rem] text-gray-11! leading-[1.38] tracking-normal'>
                    {t('invite_modal.beta_description')}
                  </Typography>
                </div>
                <div className='flex h-7 w-11 shrink-0 items-center justify-end'>
                  <Switch checked={wantsBeta} onCheckedChange={handleBetaToggle} aria-label={t('invite_modal.beta_label')} className='origin-right scale-110' />
                </div>
              </div>

              <div className='mt-6 flex items-center gap-4 rounded-lg bg-primary-2 p-4 text-primary'>
                <Icon name='lock-keyhole' aria-hidden='true' className='size-5 shrink-0' />
                <Typography variant='text-md' className='font-normal text-[0.9375rem] text-primary-12 leading-[1.38] tracking-normal'>
                  {t('invite_modal.privacy_notice')}
                </Typography>
              </div>

              <Button type='submit' className='mt-7 h-12 w-full rounded-lg font-semibold text-sm shadow-[0_12px_28px_rgba(0,82,255,0.16)]' disabled={isSubmitting}>
                {isSubmitting && <Spinner />}
                {t('invite_modal.submit')}
              </Button>
            </form>

            <div className='laptop:mt-3 flex min-w-0 flex-col items-center gap-5'>
              <aside className='w-full rounded-2xl bg-gray-2 p-7'>
                <Typography as='h3' variant='heading-2xl' className='text-balance font-bold text-gray-12 text-xl leading-[1.25] tracking-normal'>
                  {t('invite_modal.benefits.title')}
                </Typography>
                <div className='mt-5 h-px bg-gray-4' />
                <div className='mt-7 flex flex-col gap-8'>
                  {benefits.map((benefit) => {
                    return (
                      <div key={benefit.titleKey} className='flex tablet:flex-row flex-col tablet:items-start gap-4 tablet:gap-5'>
                        <div className='flex size-12 shrink-0 items-center justify-center rounded-xl bg-white text-primary shadow-sm'>
                          <Icon name={benefit.icon} aria-hidden='true' className='size-6' />
                        </div>
                        <div className='flex min-w-0 flex-col gap-2 tablet:pt-1'>
                          <Typography variant='text-lg-semibold' className='font-bold text-[0.9375rem] text-gray-12 leading-[1.3] tracking-normal'>
                            {t(benefit.titleKey)}
                          </Typography>
                          <Typography variant='text-lg' className='max-w-[240px] text-pretty font-normal text-[0.9375rem] text-gray-11! leading-[1.42] tracking-normal'>
                            {t(benefit.descriptionKey)}
                          </Typography>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </aside>

              <div className='flex items-center justify-center gap-3 text-gray-10 tabular-nums'>
                <Icon name='shield-check' aria-hidden='true' className='size-5 fill-current' />
                <Typography variant='text-md' className='font-normal text-[0.9375rem] text-gray-10 leading-[1.38] tracking-normal'>
                  {t('invite_modal.secure_environment')}
                </Typography>
              </div>
            </div>
          </div>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  );
};

export { getStorageKey as getAIUserInviteModalStorageKey };
export default AIUserInviteModal;
